import React, { useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useField } from 'formik'
import { ExperienceInputField } from '../form'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'

const InclusionWithDnd = ({
  dndType,
  index,
  name,
  canDelete,
  onDelete,
  moveItem,
  onDragEnd,
  onBlur,
  disabled = false,
}) => {
  const ref = useRef(null)
  const inputWrapRef = useRef(null)
  const [field] = useField(name)
  const [isEditing, setIsEditing] = useState(!field.value)

  useEffect(() => {
    if (!field.value) {
      setIsEditing(true)
    }
  }, [field.value])

  useEffect(() => {
    if (!isEditing || disabled || !inputWrapRef.current) {
      return undefined
    }
    const input = inputWrapRef.current.querySelector('input')
    if (input) {
      input.focus()
      input.select()
    }
    return undefined
  }, [isEditing, disabled])

  const [{ handlerId }, drop] = useDrop({
    accept: dndType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (disabled || !ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: dndType,
    item: () => {
      return { index }
    },
    canDrag: !disabled,
    end() {
      if (!disabled && typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0.4 : 1
  drag(drop(ref))

  const handleBlur = (event) => {
    if (typeof onBlur === 'function') {
      onBlur(event)
    }
    if (field.value) {
      setIsEditing(false)
    }
  }

  return (
    <div
      ref={ref}
      style={{ opacity }}
      className={`experience-lrow${isDragging ? ' is-dragging' : ''}${
        isEditing ? ' is-inline-edit' : ''
      }${disabled ? ' is-busy' : ''}`}
      data-handler-id={handlerId}>
      <span className='experience-grab' title='Drag to reorder'>
        ⋮⋮
      </span>
      {isEditing ? (
        <div
          ref={inputWrapRef}
          className='experience-lrow-copy experience-lrow-inline-field'
          draggable={true}
          onDragStart={(event) => event.preventDefault()}>
          <ExperienceInputField
            name={name}
            placeholder='Inclusion'
            capitalizeFirstLetter
            showErrorMessage={false}
            displayFlex={false}
            formGroupProps={{ className: 'mb-0' }}
            onBlur={handleBlur}
            disabled={disabled}
          />
        </div>
      ) : (
        <button
          type='button'
          className='experience-lrow-copy experience-lrow-static'
          disabled={disabled}
          onClick={() => {
            if (!disabled) setIsEditing(true)
          }}>
          <span>{field.value}</span>
        </button>
      )}
      {canDelete ? (
        <button
          type='button'
          className='experience-x'
          title='Delete inclusion'
          aria-label='Delete inclusion'
          disabled={disabled}
          onClick={onDelete}>
          <ExperienceDeleteIcon />
        </button>
      ) : null}
    </div>
  )
}

export default InclusionWithDnd
